import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface BlockNameDialogProps {
  open: boolean;
  mode: 'create' | 'rename';
  initialName?: string;
  isSubmitting?: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (name: string) => void;
}

const MAX_NAME_LENGTH = 100;

export function BlockNameDialog({
  open,
  mode,
  initialName = '',
  isSubmitting = false,
  onOpenChange,
  onSubmit,
}: BlockNameDialogProps) {
  const [name, setName] = useState(initialName);

  useEffect(() => {
    if (open) setName(initialName);
  }, [open, initialName]);

  const trimmed = name.trim();

  // Mirrors the field dialog next door, so a blocked submit is explained the same way in both.
  const missingFields: string[] = [];
  if (trimmed.length === 0) missingFields.push('Block Name');

  const canSubmit =
    missingFields.length === 0 && trimmed.length <= MAX_NAME_LENGTH && !isSubmitting;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    onSubmit(trimmed);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{mode === 'create' ? 'Add Block' : 'Rename Block'}</DialogTitle>
            <DialogDescription>
              {mode === 'create'
                ? 'Group custom fields under a named sub-section.'
                : 'Change the display name of this block.'}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-2 py-4">
            <Label htmlFor="block-name">
              Block Name{' '}
              <span className="text-destructive" aria-label="Required">
                *
              </span>
            </Label>
            <Input
              id="block-name"
              autoFocus
              aria-required="true"
              value={name}
              maxLength={MAX_NAME_LENGTH}
              placeholder="e.g. Contractor Details"
              onChange={(e) => setName(e.target.value)}
            />
            <p className="text-[11px] text-muted-foreground">
              {trimmed.length}/{MAX_NAME_LENGTH}
            </p>
          </div>

          <DialogFooter className="sm:items-center sm:justify-between">
            <p className="text-xs text-muted-foreground" aria-live="polite">
              {!isSubmitting && missingFields.length > 0
                ? `Required: ${missingFields.join(', ')}`
                : ''}
            </p>
            <div className="flex gap-2">
              <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={!canSubmit}>
                {mode === 'create' ? 'Add Block' : 'Save'}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
